import { DAEMON_URL } from "../config";
import { parseSseStream } from "./sse";
import { readError } from "./http";
import { HttpError } from "./errors";
import type { StreamEvent } from "./types";

/**
 * Reattach to a turn that is already running on the daemon.
 *
 * A turn now outlives the HTTP connection that started it, so a client that
 * navigated away, reloaded, or came back on another device can pick the same
 * turn back up. The first frame is a `snapshot` carrying everything produced so
 * far; the live tail follows it.
 *
 * The route answers with EITHER a stream or `{"streaming": false}` JSON, so
 * this branches on the content type rather than assuming a body it can parse.
 * When nothing is in flight it yields NOTHING AT ALL — not an error, not an
 * empty snapshot. The turn simply finished while the caller was away, and the
 * answer is in the database, so the caller refetches turns instead of holding a
 * stream that can never produce anything.
 *
 * `EventSource` is deliberately not used: it cannot carry this app's
 * credentials the way `fetch` does, and its automatic reconnect would turn one
 * reattach into a loop against a route that puts no ceiling on readers.
 */
export async function* reattachChat(
  project: string,
  conversationId: number,
  signal?: AbortSignal,
): AsyncGenerator<StreamEvent, void, void> {
  const url = `${DAEMON_URL}/chat/${encodeURIComponent(project)}/${conversationId}/stream`;
  let res: Response;
  try {
    res = await fetch(url, {
      headers: { Accept: "text/event-stream" },
      credentials: "include",
      // Only aborting the fetch disconnects this reader from the daemon;
      // cancelling the body reader never reaches the server.
      signal,
    });
  } catch (e) {
    // Leaving a conversation aborts on purpose. It must propagate as an
    // AbortError so the caller reads it as a departure, not as a lost
    // connection worth showing the user.
    if ((e as Error)?.name === "AbortError" || signal?.aborted) throw e;
    yield { type: "error", error: `network error: ${(e as Error).message}` };
    return;
  }

  if (res.status === 401) throw new HttpError(401, "unauthorized");
  if (res.status === 403) throw new HttpError(403, "forbidden");
  if (res.status !== 200) {
    const { message, code } = await readError(res);
    throw new HttpError(res.status, message, code);
  }

  // No live turn: nothing to yield, and not an error. The header is the
  // authority here, never the shape of the body.
  if (!(res.headers.get("content-type") ?? "").includes("text/event-stream")) return;
  // Unlike `sendChat`, an empty body is silent rather than an error frame: this
  // caller did not ask a question, so there is no answer for it to be missing.
  if (!res.body) return;

  yield* parseSseStream(res.body);
}
